import React, { useState, useEffect, useCallback, useRef } from "react";
import auth0 from "auth0-js/dist/auth0.min.js";
import PasswordResetRouter from "./components/PasswordReset";
import RegistrationRouter from "./components/Registration";
import Login from "./components/Login";
import Loading from "./components/Loading";
import Language from "./components/Language";
import ApplicationError from './ApplicationError';
import ErrorComponent from "./components/Error";
import ErrorUtils from './utils/ErrorUtils';

import { LanguageProvider } from "./contexts/Language";
import { TitleProvider } from "./contexts/Title";
import { UtilProvider } from "./contexts/Util";
import { UserDataProvider } from "./contexts/UserData";
import { StatusProvider } from "./contexts/Status";

import { v4 } from "uuid";
import qs from "query-string";
import AppConfig from "./AppConfig";

export default function App() {
  const [ loading, setLoading ] = useState(true);
  const [ global_error, setGlobalError ] = useState("");
  const [ api_token, setApiToken ] = useState("");
  const [ trace_id, ] = useState(v4());
  const [ header_title, setTitle ] = useState("");
  const [ lang, setLang ] = useState("");
  const [ client_id, setClientId ] = useState("");
  const [ document_id, setDocumentId ] = useState([]);
  const [ tags, setTags ] = useState({});
  const [ config, setConfig ] = useState({});
  const [ return_url, setReturnUrl ] = useState("");
  const [ user_data, setUserData ] = useState({});
  const [ language_menu_open, setLanguageMenuOpen ] = useState(false);
  const [ show_doc, setShowDoc ] = useState({});
  const [ additional_tos_open, setAdditionalTosOpen ] = useState(false);
  const [ page_status, setPageStatus ] = useState("/login");
  const [ auth0_clients, setAuth0Clients ] = useState(null);
  const [ auth0_sns_conns, setAuth0SNSConns ] = useState(null);
  const [ default_phone_region, setDefaultPhoneRegion ] = useState(null);
  const [ view_disabled, setViewDisabled ] = useState({});
  const [ password_valid, setPasswordValid ] = useState(false);

  const history_store = useRef({});
  const browser_ope = useRef(false);

  /**
   * エラーメッセージフォーマット
   * @param {number} status HTTPステータスコード
   * @param {object} json エラー格納json
   * @returns エラーメッセージ
   */
  function formatError(status, json) {
    let code = (json.code ?? false) ? json.code : '-';
    let message = (json.message ?? false) ? json.message : '-';
    let details = (json.details ?? false) ? json.details : undefined;
    return `status=${status}, code=${code}, message=${message}` + ((details ?? false) ? `,details=${details}` : '');
  }

  /**
   * スリープ処理
   * @param {int} ms スリープ時間（ミリ秒）
   */
  const sleep = async(ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const request = useCallback( async ( uri, options ) => {
    let opt = {...options};
    opt.headers = {
      ...opt.headers,
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-PCPF-ApplicationTraceId": trace_id,
      "Accept-Language": lang,
    };

    if( api_token ) {
      opt.headers["X-API-Token"] = api_token;
    }

    const res = await fetch(uri, opt);
    // メンテナンス中を示すレスポンスであれば、指定のリダイレクト先に遷移する。
    if (res.status === 503) {
      const resBody = await res.clone().json();
      if ("maintenanceRedirection" in resBody) {
        window.location.href = resBody.maintenanceRedirection;
        // 後続処理が非同期で走るため、少し遅らせる
        await sleep(1000);
      }
    }
    return res;
  }, [api_token, trace_id, lang]);

  const requestV2 = useCallback( async ( uri, options ) => {
    let opt = {...options};
    opt.headers = {
      ...opt.headers,
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Accept-Language': lang,
      'x-pcpf-applicationtraceid': trace_id,
      'x-authz-clientid': client_id
    };

    if( api_token ) {
      opt.headers["x-authz-sessionid"] = api_token;
    }

    return fetch( uri, opt );
  }, [api_token, trace_id, client_id, lang]);

  const email_is_valid = (email) => {
    return email.length > 0 && /^(("[^"]+?"@)|([0-9a-zA-Z]@)|(([0-9a-zA-Z]((\.(?!\.))|[-!#$%&'*+/=?^`{}|~\w])*)([0-9a-zA-Z-_])@))((\[(\d{1,3}\.){3}\d{1,3}\])|(([0-9a-zA-Z][-\w]*[0-9a-zA-Z]*\.)+[a-zA-Z0-9]{2,24}))$/.test(email);
  };
  const phone_is_valid = (in_phone) => {
    if (!in_phone) {
      return false;
    }
    let phone = in_phone.replace("+", "");
    if (8 > phone.length > 15) {
      return false;
    }
    if (!(/^[0-9]+$/.test(phone))) {
      return false;
    }
    return true;
  };

  // ドキュメント情報の読み込み
  const loadDocument = async (doc_id, country, doc_list) => {
    try {
      let res = await request(`${AppConfig.API_URL}/api/v1/document/${country}/${doc_id}?client_id=${client_id}`);
      if( res.status !== 200 ) {
        console.log("couldn't load doc " + doc_id);
        throw new Error("invalid_agreement_info");
      }
      let json = await res.json();
      let found = false;

      for( let doc of doc_list ) {
        if( doc.document_id === doc_id ) {
          doc.content = json.content;
          doc.version = json.version;
          doc.loaded = found = true;
          break;
        }
      }
      if( ! found ) {
        console.log("didn't find doc " + doc_id);
        throw new Error("invalid_agreement_info");
      }
    }
    catch(err) {
      setGlobalError(err.message);
    }
  };


  // 指定されたドキュメントは設定ファイルに存在チェック
  const loadRegisterDocuments = async () => {
    for( let doc of config.agreement.register ) {
      if( ! doc.loaded && ! global_error && ! doc.loading ) {
        doc.loading = true;
        await loadDocument( doc.document_id, doc.country, config.agreement.register );
        doc.loading = false;
        doc.loaded = true;
      }
      if( global_error ) {
        break;
      }
    }
  };

  // 設定ファイル読み込み
  const loadConfig = useCallback( async () => {
    const metaconfig = document.querySelector("meta[name='config']").getAttribute("value");
    const gconfig = JSON.parse(decodeURIComponent(escape(window.atob(metaconfig))));

    let client_id = gconfig ? gconfig.clientID : null;

    try {
      let res = await request(`${AppConfig.API_URL}/settings/${client_id}/config`);

      if( res.status !== 200 ) {
        throw new Error( res.statusText );
      }

      let config = await res.json();
      config.static_url = AppConfig.STATIC_BASE_URL;
      config.audience = `https://${config.m2m_domain}/api/v2/`;

      return {...gconfig, ...config};
    }
    catch(err) {
      console.log(err.message);
      throw new Error("page_not_found");
    }
  }, [request]);

  // 言語データの読み込み
  const loadTags = useCallback(async (lang) => {
    try {
      if( ! tags[lang] || ! tags[lang]._loaded ) {
        let res = await request(`${AppConfig.API_URL}/settings/${client_id}/lang/${lang}`);
        if (res.status !== 200) {
          throw new Error("language_file_not_found");
        }
        setTags({ ...tags, [lang]: { ...await res.json(), _loaded: true } });
      }
      return true;
    }
    catch(err) {
      throw new Error(err.message);
    }
  }, [client_id,tags,request]);


  const findTag = ( key, from )  => {
    if( ! from ) {
      return null;
    }
    if( ! key ) {
      return from;
    }

    let keys = key.split(".");

    from = from[ keys[0] ];
    keys.shift();
    return findTag( keys.join("."), from )
  };


  /**
   * 指定された言語とタグで文言取得
   * @param {string} lang 言語コード
   * @param {string} tag タグ（翻訳リソースのキー）
   * @param {boolean} withSpan spanタグを強制的に差し込む場合、true
   * @returns JSX
   */
  const getLangTag = (lang, tag, withSpan = false) => {
    try {
      let text = findTag( tag, tags[lang] );

      let insertSpan = withSpan;
      // 仮登録/仮変更エラーダイアログの文言は、spanタグで囲まないようにする
      // TODO より良いロジックを検討する、css側で対応してこの処理自体なくせないか、等々検討したい
      if (!insertSpan) {
        insertSpan = text && (typeof text === 'string') && text.match(/\n/g) && !tag.match(/already_registered_tmpreg/g) && !tag.match(/already_registered_reserved/g);
      }
      if (insertSpan) {
        text = <span style={{ whiteSpace: "pre-line" }}>{text}</span>
      }

      return text;
    }
    catch(err) {
      console.log(err);
      return null;
    }
  };

  /**
   * 指定されたタグの文言取得
   * @param {string} tag タグ（翻訳リソースのキー）
   * @param {boolean} withSpan spanタグを強制的に差し込む場合、true
   * @returns JSX
   */
  const getTag = (tag, withSpan = false) => {
    return getLangTag( lang, tag, withSpan);
  }

  // 言語別名変換
  const getLangAlias = (lang) => {
    if (lang && 'language_alias' in config) {
      if (lang in config.language_alias) {
        lang = config.language_alias[lang];
      } else if (lang.includes('-')) {
        let ll = lang.split('-')[0];
        if (ll in config.language_alias) {
          lang = config.language_alias[ll];
        }
      }
    }
    return lang;
  };

  // UI の言語設定
  const changeLanguage = useCallback(async (l) => {
    let lang = l ? l.toLowerCase() : l;
    lang = getLangAlias(lang);

    if( lang && config.languages.includes(lang) ) {
      let res = await loadTags( lang );

      if( res ) {
        sessionStorage.setItem("lang", lang);
        setLang( lang );
        return;
      }
      else {
        lang = null;
      }
    }
    else {
      lang = null;
    }

    // 記録された言語設定を確認
    if (sessionStorage.getItem("lang")) {
      lang = sessionStorage.getItem("lang");
      let res = null;
      try {
        res = await loadTags( lang );
      }
      catch (err) {
        lang = "en";
        res = await loadTags( lang );
      }

      if( res ) {
        sessionStorage.setItem("lang", lang);
        setLang( lang );
        return;
      }
    }

    // ブラウザー言語設定を取得
    let langs = [];

    for( const key of ["languages", "language", "userLanguage"] ) {
      if( navigator[key] ) {
        if( Array.isArray(navigator[key]) ) {
          langs = langs.concat(navigator[key]);
        }
        else if( ! langs.includes(navigator[key]) ) {
          langs.push(navigator[key]);
        }
      }
    }

    for( const l of langs ) {
      // ロケール含めて検査
      let ll = l.toLowerCase();
      ll = getLangAlias(ll);
      if (config.languages.includes(ll)) {
        let res = await loadTags(ll);
        if (res) {
          sessionStorage.setItem("lang", ll);
          setLang(ll);
          return;
        }
      }

      // en-US とかをenのみにする
      const short_lang = l.split("-")[0];

      if( config.languages.includes(short_lang) ) {
        let res = await loadTags( short_lang );

        if( res ) {
          sessionStorage.setItem("lang", short_lang);
          setLang( short_lang );
          return;
        }
      }
    }

    if( ! lang ) {
      console.log("! lang");
      sessionStorage.setItem("lang", config.default_language);
      setLang(config.default_language);

      let res = await loadTags( config.default_language );
      if( ! res ) {
        throw new Error("No language file");
      }
    }
  },[config, loadTags, setLang]);

  const checkEmail = async (url, form_data) => {
    let result = await request(url, {
      method: "POST",
      body: JSON.stringify({ ...form_data, client_id: user_data.client_id, language: lang })
    })
      .then(res => {
        return res.json()
          .then(json => {
            if (res.status === 200) {
              setUserData({ ...user_data, ...form_data, token: json.token });
              setApiToken(json.api_token);
              return true;
            } else if (res.status === 404) {
              throw new ApplicationError({ code: 'not_registered' }, `Not registered. (${formatError(res.status, json)})`);
            } else if (res.status === 409) {
              if (json.message === 'already_registered_tmpreg') {
                throw new ApplicationError({ code: 'already_registered_tmpreg' }, `Already registered. (${formatError(res.status, json)})`);
              } else if (json.message === 'already_registered_reserved') {
                throw new ApplicationError({ code: 'already_registered_reserved' }, `Already registered. (${formatError(res.status, json)})`);
              }
              throw new ApplicationError({ code: 'already_registered' }, `Already registered. (${formatError(res.status, json)})`);
            } else if (res.status === 400) {
              if (('message' in json) && json.message === 'invalid_request') {
                throw new ApplicationError({ code: 'invalid_operation_state'}, `Google reCAPTCHA score does not pass. (${formatError(res.status, json)})`);
              }
            }
            throw new ApplicationError({ code: 'system_error' }, `System error. Unexpected error occurred. (${formatError(res.status, json)})`);
          });
      })
      .catch(err => {
        if (err instanceof ApplicationError) {
          throw err;
        }
        throw new ApplicationError({ code: 'fetch_error' }, `Fetch error. (${err.message})`);
      });

    return result;
  };
  
  const checkPhone = async (url, form_data) => {
    let result = await request(url, {
      method: "POST",
      body: JSON.stringify({ ...form_data, client_id: user_data.client_id, language: lang })
    })
      .then(res => {
        return res.json()
          .then(json => {
            if (res.status === 200) {
              setUserData({ ...user_data, ...form_data, token: json.token, phone_use: json.use_phone});
              setApiToken(json.api_token);
              return true;
            } else if (res.status === 404) {
              throw new ApplicationError({ code: 'not_registered_phone' }, `Not registered. (${formatError(res.status, json)})`);
            } else if (res.status === 409) {
              throw new ApplicationError({ code: 'already_registered_phone' }, `Already registered. (${formatError(res.status, json)})`);
            } else if (res.status === 400) {
              if (('message' in json) && json.message === 'invalid_captcha_token') {
                throw new ApplicationError({ code: 'invalid_operation_state'}, `Google reCAPTCHA score does not pass. (${formatError(res.status, json)})`);
              }
              else if (('message' in json) && json.message === 'invalid_phone') {
                throw new ApplicationError({ code: 'invalid_phone'}, `Invalid Phone Number. (${formatError(res.status, json)})`);
              }
            }
            throw new ApplicationError({ code: 'system_error' }, `System error. Unexpected error occurred. (${formatError(res.status, json)})`);
          });
      })
      .catch(err => {
        if (err instanceof ApplicationError) {
          throw err;
        }
        throw new ApplicationError({ code: 'fetch_error' }, `Fetch error. (${err.message})`);
      });

    return result;
  };

  const passwordResetEmail = async (form_data) =>  {
    await checkEmail(`${AppConfig.API_URL}/api/v1/password/recover`, form_data );
  };
    const passwordResetPhone = async (form_data) =>  {
    await checkPhone(`${AppConfig.API_URL}/api/v1/password/phone/recover`, form_data );
  };

  // メールフォームのデータを登録
  // （年齢確認と同意情報を含む）
  const registerEmail = async (form_data) => {
    return await checkEmail(`${AppConfig.API_URL}/api/v1/register/email`, form_data);
  };
  const registerPhone = async (form_data) => {
    return await checkPhone(`${AppConfig.API_URL}/api/v1/register/phone`, form_data);
  };

  //Google reCAPTCHA検証
  const checkRecaptcha = async (form_data) => {
    let result = await request(`${AppConfig.API_URL}/api/v1/util/recaptcha`, {
      method: "POST",
      body: JSON.stringify({ ...form_data, client_id: user_data.client_id, language: lang })
    })
      .then(res => {
        return res.json()
        .then(json => {
          if (res.status === 200) {
            return json;
          }
          throw new ApplicationError({ code: 'system_error' }, `A system error occurred. (${formatError(res.status, json)})`);
        });
      })
      .catch(err => {
        if (err instanceof ApplicationError) {
          throw err;
        }
        throw new ApplicationError({ code: 'fetch_error' }, `Fetch error. (${err.message})`);
      });

    return result;
  };


  const checkCode = async (url, code) => {
    let udata = { ...user_data };
    udata.code = code;
    udata.code_confirmed = false;

    let result = await request(url, {
      method: "POST",
      body: JSON.stringify({
        token: udata.token,
        code: udata.code,
        email: udata.email,
        client_id: client_id,
        language: lang,
      })
    })
      .then(res => {
        return res.json()
          .then(json => {
            if (res.status === 200) {
              udata.code_confirmed = json.code_confirmed;
              udata.state = json.state;
              setUserData(udata);
              return true;
            } else if (res.status === 401) {
              if (json.message === 'invalid_code') {
                // OTP入力誤り（3回未満）
                return null;
              } else {
                // OTP失効（失効済・期限切れ・３回誤り）※存在しないケースも含める
                throw new ApplicationError({ code: 'expired_code' }, `Code expired. (${formatError(res.status, json)})`);
              }
            }
            throw new ApplicationError({ code: 'system_error' }, `System error. Unexpected error occurred. (${formatError(res.status, json)})`);
          });
      })
      .catch(err => {
        if (err instanceof ApplicationError) {
          throw err;
        }
        throw new ApplicationError({ code: 'fetch_error' }, `Fetch error. (${err.message})`);
      });
    return result;
  };
  const checkCPhoneCode = async (url, code) => {
    let udata = { ...user_data };
    udata.code = code;
    udata.code_confirmed = false;

    let result = await request(url, {
      method: "POST",
      body: JSON.stringify({
        token: udata.token,
        code: udata.code,
        phone: udata.phone,
        client_id: client_id,
        language: lang,
      })
    })
    .then(res => {
      return res.json()
        .then(json => {
          if (res.status === 200) {
            udata.code_confirmed = json.code_confirmed;
            udata.state = json.state;
            setUserData(udata);
            return true;
          } else if (res.status === 401) {
            if (json.message === 'invalid_code') {
              // OTP入力誤り（3回未満）
              return null;
            }
            else if (json.message === 'expired_code') {
              // 確認コードの検証可能時間超過
              throw new ApplicationError({ code: 'max_check_attempts_reached' }, `Code Expired. (${formatError(res.status, json)})`);
            }
          } else if (res.status === 429) {
            // 確認コードの検証可能上限到達
            throw new ApplicationError({ code: 'max_check_attempts_reached' }, `Max check attempts reached. (${formatError(res.status, json)})`);
          }
          throw new ApplicationError({ code: 'system_error' }, `System error. Unexpected error occurred. (${formatError(res.status, json)})`);
        });
    })
    .catch(err => {
      if (err instanceof ApplicationError) {
        throw err;
      }
      throw new ApplicationError({ code: 'fetch_error' }, `Fetch error. (${err.message})`);
    });
    return result;
  };

  const registerCode = async (code) => {
    return await checkCode(`${AppConfig.API_URL}/api/v1/register/code`, code );
  };
  const registerPhoneCode = async (code) => {
    return await checkCPhoneCode(`${AppConfig.API_URL}/api/v1/register/phone/code`, code );
  };

  const registerUser = async (user) => {
    let req_user = {
      ...user,
      language: lang,
      client_id: client_id,
      state: user_data.state
    };

    const result = await request(`${AppConfig.API_URL}/api/v1/register/password`, {
      method: "POST",
      body: JSON.stringify(req_user)
    }).then(res => {
      return res.json()
        .then(json => {
          if (res.status === 200) {
            user_data.password = user.password;
            delete user_data.state;
            setUserData(user_data);
            return true;
          } else if (res.status === 409) {
            throw new ApplicationError({ code: 'already_registered' }, `Already registered. (${formatError(res.status, json)})`);
          } else if (res.status === 400) {
            if (('message' in json) && json.message === 'invalid_request') {
              throw new ApplicationError({ code: 'invalid_operation_state'}, `An invalid operation has been detected.. (${formatError(res.status, json)})`);
            }
          }
          throw new ApplicationError({ code: 'system_error' }, `System error. Unexpected error occurred. (${formatError(res.status, json)})`);
        });
    })
    .catch(err => {
      if (err instanceof ApplicationError) {
        throw err;
      }
      throw new ApplicationError({ code: 'fetch_error' }, `Fetch error. (${err.message})`);
    });
    return result;
  };

  const registerPhoneUser = async (user) => {
    let req_user = {
      ...user,
      language: lang,
      client_id: client_id,
      state: user_data.state
    };

    const result = await request(`${AppConfig.API_URL}/api/v1/register/phone/password`, {
      method: "POST",
      body: JSON.stringify(req_user)
    }).then(res => {
      return res.json()
        .then(json => {
          if (res.status === 200) {
            user_data["phone_registerd"] = json.username;
            user_data.password = user.password;
            delete user_data.state;
            setUserData(user_data);
            return true;
          } else if (res.status === 409) {
            throw new ApplicationError({ code: 'already_registered_phone' }, `Already registered. (${formatError(res.status, json)})`);
          } else if (res.status === 400) {
            if (('message' in json) && json.message === 'invalid_request') {
              throw new ApplicationError({ code: 'invalid_operation_state'}, `An invalid operation has been detected.. (${formatError(res.status, json)})`);
            }
          }
          throw new ApplicationError({ code: 'system_error' }, `System error. Unexpected error occurred. (${formatError(res.status, json)})`);
        });
    })
      .catch(err => {
        if (err instanceof ApplicationError) {
          throw err;
        }
        throw new ApplicationError({ code: 'fetch_error' }, `Fetch error. (${err.message})`);
      });
    return result;
  };

  const passwordResetCode = async (code) => {
    return await checkCode(`${AppConfig.API_URL}/api/v1/password/code`, code );
  };
  const passwordResetPhoneCode = async (code) => {
    return await checkCPhoneCode(`${AppConfig.API_URL}/api/v1/password/phone/code`, code );
  };

  const passwordReset = async (password) => {
    await request(`${AppConfig.API_URL}/api/v1/password/reset`, {
      method: "POST",
      body: JSON.stringify({
        email: user_data.email,
        client_id: client_id,
        token: user_data.token,
        password: password,
        state: user_data.state
      })
    })
      .then(res => {
        return res.json()
          .then(json => {
            if (res.status === 200) {
              user_data.password = password;
              delete user_data.state;
              setUserData(user_data);
              return true;
            } else if (res.status === 400) {
              if (('message' in json) && json.message === 'invalid_request') {
                throw new ApplicationError({ code: 'invalid_operation_state'}, `An invalid operation has been detected.. (${formatError(res.status, json)})`);
              }
            }
            throw new ApplicationError({ code: 'system_error' }, `System error. Unexpected error occurred. (${formatError(res.status, json)})`);
          });
      })
      .catch(err => {
        if (err instanceof ApplicationError) {
          throw err;
        }
        throw new ApplicationError({ code: 'fetch_error' }, `Fetch error. (${err.message})`);
      });
  }

  const passwordPhoneReset = async (password) => {
    await request(`${AppConfig.API_URL}/api/v1/password/phone/reset`, {
      method: "POST",
      body: JSON.stringify({
        phone: user_data.phone,
        client_id: client_id,
        token: user_data.token,
        password: password,
        state: user_data.state
      })
    })
      .then(res => {
        return res.json()
          .then(json => {
            if (res.status === 200) {
              user_data.password = password;
              delete user_data.state;
              setUserData(user_data);
              return true;
            } else if (res.status === 400) {
              if (('message' in json) && json.message === 'invalid_request') {
                throw new ApplicationError({ code: 'invalid_operation_state'}, `An invalid operation has been detected.. (${formatError(res.status, json)})`);
              }
            }
            throw new ApplicationError({ code: 'system_error' }, `System error. Unexpected error occurred. (${formatError(res.status, json)})`);
          });
      })
      .catch(err => {
        if (err instanceof ApplicationError) {
          throw err;
        }
        throw new ApplicationError({ code: 'fetch_error' }, `Fetch error. (${err.message})`);
      });
  }

  // パスワードチェック処理
  const checkPasswordPolicy = async (password, email, phone) => {
    const result = await request(`${AppConfig.API_URL}/api/v1/util/passwordpolicy`, {
      method: "POST",
      body: JSON.stringify({
        client_id: client_id,
        password: password,
        email: email,
        phone: phone
      })
    })
      .then(res => {
        return res.json()
          .then(json => {
            if (res.status === 200) {
              setPasswordValid(json.result)
              return json.result;
            }
            throw new ApplicationError({ code: 'system_error' }, `System error. Unexpected error occurred. (${formatError(res.status, json)})`);
          });
      })
      .catch(err => {
        if (err instanceof ApplicationError) {
          throw err;
        }
        throw new ApplicationError({ code: 'fetch_error' }, `Fetch error. (${err.message})`);
      });
    return result;
  }

  const clearUserData = () => {
    setUserData({...user_data, email: "", password: "" });
  };

  const login = (username, password, setOccuredError, errorData, setInputError, cb, in_idType) => {
    disableAcceptBtn();
    const auth_params = {
      overrides: {
        __tenant: config.auth0Tenant,
        __token_issuer: config.authorizationServer.issuer
      },
      domain: config.auth0Domain,
      clientID: config.clientID,
      responseType: "code",
      redirectUri: config.is_language_overwritten ? `${config.callbackURL}?lang=${lang}` : config.callbackURL,
      audience: config.audience,
      ...config.internalOptions
    };

    const auth = new auth0.WebAuth(auth_params);
    const login_data = { username: username, password: password, realm: config.realm, lang: lang };

    auth.login(login_data, (err) => {
      console.log(err);
      cb();
      if (err) {
        let invalid_up_msg = null;
        if (in_idType && in_idType === "phone") {
          invalid_up_msg = 'invalid_phone_and_password';
        }
        else {
          invalid_up_msg = 'invalid_username_and_password';
        }

        if (err.code === "invalid_user_password") {
          console.log('The username or password is invalid.');
          setInputError(invalid_up_msg);
        }
        else if (err.code === "access_denied" && err.description.match(/Wrong username or password./)) {
          // パスワード誤り
          console.log('The username or password is invalid.');
          setInputError(invalid_up_msg);
        }
        else if (err.code === "access_denied" && err.description.match(/401UnauthorizedLogin/)) {
          // TODO: こちらの条件はSTEP1仕様
          console.log('The usernameor password is invalid.');
          setInputError(invalid_up_msg);
        }
        else if (err.code === "access_denied" && err.description.match(/^statusCode=401,code=Unauthorized,/)) {
          // メアド未登録
          // TODO: こちらの条件はSTEP2仕様
          console.log('The username or password is invalid.');
          setInputError(invalid_up_msg);
        }
        else if (err.code === "access_denied" && err.description.match(/^statusCode=401,code=PasswordResetRequired,/)) {
          // TODO: こちらの条件はSTEP2仕様 (パスワードリセット誘導トリガー)
          console.log('Password reset is required.');

          // パスワードリセット誘導へハンドリングする
          errorData.current = ErrorUtils.getErrorInfo(new ApplicationError({ code: 'not_migrated' }, 'The user is not migrated.'));
          setOccuredError(true);
        }
        else if (err.code === "too_many_attempts") {
          // ブルートフォースアタック検知によるブロック、画面上はメアド/PW誤りとして表示する
          console.log('The account is blocked due to too many attempts to sign in.');
          setInputError(invalid_up_msg);
        }
        else {
          errorData.current = ErrorUtils.getErrorInfo(new ApplicationError({ code: 'system_error' }, 'System error. Unexpected error occurred in Auth0.'));
          setOccuredError(true);
        }
      }
      disableAcceptBtn(false);
    });
  };

  const loockupPhoneNo = async (in_phone) => {
    const result = await request(`${AppConfig.API_URL}/api/v1/util/phonelookup`, {
      method: "POST",
      body: JSON.stringify({
        client_id: client_id,
        phone: in_phone
      })
    })
    .then(res => {
      return res.json()
        .then(json => {
          if (res.status === 200) {
            if (json.use) {
              return json.use;
            }
            return false;
          }
          else if (res.status === 400) {
            throw new ApplicationError({ code: 'invalid_phone'}, `Invalid Phone Number. (${formatError(res.status, json)})`);
          }
          throw new ApplicationError({ code: 'system_error' }, `System error. Unexpected error occurred. (${formatError(res.status, json)})`);
        });
    })
    .catch(err => {
      if (err instanceof ApplicationError) {
        throw err;
      }
      throw new ApplicationError({ code: 'fetch_error' }, `Fetch error. (${err.message})`);
    });
    return result;
  }

  const socialLogin = async (in_connection) => {
    if (!checkDisabledSocialBtn()) {
      return;
    }
    disableAcceptBtn();
    disableSocialAcceptBtn();
    
    let key = config.internalOptions.code_challenge;
    const res = await request(`${AppConfig.API_URL}/settings/apl/${client_id}/${key}`, {
      method: "POST",
      body: JSON.stringify({
        value: lang
      })
    });
    const json = await res.json();
    if( res.status !== 200 ) {
      console.log("couldn't save ui_locale");
    }

    const auth_params = {
      overrides: {
        __tenant: config.auth0Tenant,
        __token_issuer: config.authorizationServer.issuer
      },
      domain: config.auth0Domain,
      clientID: config.clientID,
      responseType: "code",
      redirectUri: config.callbackURL,
      audience: config.audience,
      ui_locales: lang,
      ...config.internalOptions
    };

    window.history.replaceState("", "", "/login");
    const auth = new auth0.WebAuth(auth_params);
    auth.authorize({
      connection: in_connection,
      ui_locales: lang
    }, function(in_err) {
      if (in_err) {
        console.log(in_err);
      }
      disableAcceptBtn(false);
      disableSocialAcceptBtn(false);
    });
  }

  const loading_config = useRef(false);
  
  /**
   * クライアント情報解析
   * 
   * 取得したクライアント情報JSファイルの内容を解析し、
   * JSONオブジェクトとして返す
   * 
   * @param {*} in_expr 
   * @returns 
   */
  const parseAuth0Clients = (in_expr) => {
    if (in_expr === undefined || in_expr === null) {
      console.log("### undefined");
      return {};
    }
    else if (typeof in_expr != "string") {
      console.log("### not string");
      return {};
    }
    else if (in_expr.startsWith("Auth0.setClient(") !== true) {
      console.log("### not startwith");
      return {};
    }
    let data = in_expr.substring("Auth0.setClient(".length, in_expr.indexOf(");"));
    console.log("### clients(str) : " + data);
    let json = JSON.parse(data);
    console.log("### clients(json)");
    console.dir(json);
    return json;
  }

  useEffect( () => {
    (async () => {
      if( ! client_id && ! loading_config.current ) {
        loading_config.current = true;

        try {
          const config = await loadConfig();
          // クライアント情報の取得
          let now = new Date();
          let clienturl = "https://" + config.auth0Domain + "/client/" + config.clientID + ".js?t" + now.getTime();
          fetch(clienturl).then(r=>{return r.text()}).then(t=>{
              console.log(t);
              setAuth0Clients(parseAuth0Clients(t));
          });
          setClientId( config.clientID );
          setConfig(config);
          setReturnUrl( config.callbackURL );
          setDocumentId( document_id );
          if (!user_data.login_id_type) {
            user_data.login_id_type = "email";
          }
          setUserData({...user_data, client_id: config.clientID, return_url: config.callbackURL, tenant: config.auth0Tenant});
        }
        catch(err) {
          console.log("set global error: " + err.message);
          setGlobalError(err.message);
        }

        loading_config.current = false;
      }

    })();
  }, [user_data,setUserData,loading_config,client_id,setClientId,document_id,return_url,loadConfig,setConfig,setLoading,setReturnUrl,setDocumentId,setGlobalError]);

  const loading_tags = useRef(false);

  // 起動時の画面表示処理
  useEffect( () => {
    (async () => {
      let { first, idtype, connection, disabled } = qs.parse( window.location.search );

      let options = {
        disabledSocial: false,
        disabledSignup: false,
        disabledLogin: false,
        disabledPwReset: false,
        disabledLoginIDType: false,
        targetConnection: "PanasonicID-Authentication"
      };
      // ----------------------------
      // Extract Query String
      // ----------------------------
      // 起動時画面の指定（signup、passwordresetでない限りはlogin）
      if (first !== undefined && first !== null) {
        if (first.toLowerCase() === "signup") {
          setPageStatus("/register/form");
        } else if (first.toLowerCase() === "passwordreset") {
          setPageStatus("/password/recover");
        }
      }
      // 起動時画面ログインID種別指定 (email, phone)
      if (idtype) {
        if (idtype.toLowerCase() === "phone") {
          user_data["login_id_type"] = "phone";
        }
      }

      // Connectionの選択
      if (connection !== undefined && connection !== null && connection === "PanasonicID-Authentication") {
        options.disabledSocial = true;
      }
      // 無効項目の指定
      if (disabled !== undefined && disabled !== null && disabled.length > 0) {
        let items = disabled.split(",");
        for (let item of items) {
          if (item === "reg") {
            options.disabledSignup = true;
          }
          else if (item === "pwr") {
            options.disabledPwReset = true;
          }
          else if (item === "login") {
            options.disabledLogin = true;
          }
          else if (item === "social") {
            options.disabledSocial = true;
          }
          else if (item === "id_type") {
            options.disabledLoginIDType = true;
          }
        }
      }
      setViewDisabled(options);
    })();
  }, []);

  // 起動時の言語設定処理
  useEffect( () => {
    (async () => {
      let { lang } = qs.parse( window.location.search );
      if( client_id && config.languages && loading && ! loading_tags.current ) {
        loading_tags.current = true;
        try {
          await changeLanguage( lang );
        }
        catch(err) {
          console.log(err);
          setGlobalError(err.message);
        }
        loading_tags.current = false;
        setLoading(false);
      }
    })();
  }, [client_id, config, loading, changeLanguage]);

  const extractSNSConns = (in_clients) => {
    console.log("conns extract");
    if (!in_clients['strategies']) {
      return;
    }
    console.log("====================================");
    console.log(" Connections")
    let tmpConns = [];
    for (let item of in_clients['strategies']) {
      console.log("----------------------------------");
      console.log(item["name"]);
      if (item["name"] === "auth0") {
        console.log("skip auth0 conns");
        continue;
      }
      console.dir(item['connections']);
      for (let conn of item['connections']) {
        tmpConns.push(conn);
      }
    }
    console.log("====================================");
    console.log(" Extract result")
    console.log("----------------------------------");
    console.dir(tmpConns);
    console.log("====================================");
    return tmpConns;
  }
  
  const checkDisabledSocialBtn = () => {
    let btns = document.querySelectorAll(".social_btn");
    let res = true;
    btns.forEach(elm => {
      if (elm.classList.contains("disabledbtn")) {
        res = false;
        return;
      }
    });
    return res;
  }

  /**
   * Accept系ボタンの有効/無効切り替え
   */
  const disableAcceptBtn = (in_id, in_isDisabled) => {
    if (in_isDisabled === undefined || in_isDisabled === null || in_isDisabled !== false) {
        in_isDisabled = true;
    }
    let btns = document.querySelectorAll("#" + in_id + " .acceptbtn");
    btns.forEach(elm => {
        elm.disabled = in_isDisabled;
        if (in_isDisabled) {
            elm.classList.add("disabledbtn");
        }
        else {
            elm.classList.remove("disabledbtn");
        }
    });
  }
  /**
   * Social Accept系ボタンの有効/無効切り替え
   */
  const disableSocialAcceptBtn = (in_isDisabled) => {
    if (in_isDisabled === undefined || in_isDisabled === null || in_isDisabled !== false) {
        in_isDisabled = true;
    }
    let btns = document.querySelectorAll(".social_btn");
    btns.forEach(elm => {
        elm.disabled = in_isDisabled;
        if (in_isDisabled) {
            elm.classList.add("disabledbtn");
        }
        else {
            elm.classList.remove("disabledbtn");
        }
    });
  }

  useEffect( () => {
    (async () => {
      //console.log("UseEffect conns extract");
      if (!auth0_clients) {
        return;
      }
      setAuth0SNSConns(extractSNSConns(auth0_clients));
    })();
  }, [client_id, config, auth0_clients]);

  /**
   * 認証画面表示モードの判定と設定
   */
  useEffect( () => {
    (async () => {
      //console.log("---------------------------------");
      //console.log("UseEffect authn mode setting");
      //console.log(config.authn_mode);
      //console.dir(view_disabled);
      //console.dir(default_phone_region);
      //console.log("---------------------------------");
      let options = view_disabled;
      // 画面動作モードの反映
      if (config.authn_mode !== undefined && config.authn_mode != null && config.authn_mode.length > 0) {
        if (config.authn_mode.toLowerCase() === "login_only") {
          options.disabledSignup = true;
        }
        else if ((config.authn_mode.toLowerCase() === "signin_only")
                  || (config.authn_mode.toLowerCase() === "signup_only")) {
          options.disabledLogin = true;
          setPageStatus("/register/form");
        }
        setViewDisabled(options);
      }
      // ログインID種別の反映
      if (config.login_id_type !== undefined && config.login_id_type != null && config.login_id_type.length > 0) {
        let cfgType = config.login_id_type.toLowerCase();
        if (cfgType === "phone") {
          user_data["login_id_type"] = "phone";
        }
        else if (cfgType === "email_only") {
          user_data["login_id_type"] = "email";
          options.disabledLoginIDType = true;
          setViewDisabled(options);
        }
        else if (cfgType === "phone_only") {
          user_data["login_id_type"] = "phone";
          options.disabledLoginIDType = true;
          setViewDisabled(options);
        }
      }
      // 国/地域コード初期値の読み込み
      if (!default_phone_region) {
        let tmpRegion = {"no": null, "country": null};
        if (config.default_region_phone_no) {
          tmpRegion.no = config.default_region_phone_no;
        }
        if (config.default_region_phone_country) {
          tmpRegion.country = config.default_region_phone_country;
        }
        if (tmpRegion.no || tmpRegion.country) {
          setDefaultPhoneRegion(tmpRegion);
        }
      }
    })();
  }, [config, user_data, view_disabled, default_phone_region]);

  useEffect( () => {
    window.addEventListener('popstate', function(e) {
      // 疑似的な画面遷移を行っているため、通常の履歴移動（URL移動）では画面が更新されない。
      // そのため、ブラウザ操作が行われた場合はPageStausの変更を行い画面遷移を行う
      // 規約表示など画面遷移を伴わない場合は、対象表示仕様に則り非表示を行う
      /*console.log("### ==========================");
      console.log('### ブラウザの履歴操作を検知しました。');
      console.log("### length: " + window.history.length);
      console.log("### pathname: " + window.location.pathname);
      console.log("### page_status: " + page_status);
      console.log("### -------------------------");
      console.log("### stored_pathname: " + history_store.current.pathname);
      console.log("### stored_page_status: " + history_store.current.page_status);
      console.log("### ==========================");*/
      // 規約文書が表示されている場合
      // 規約文書の表示には履歴追加が行われない（URL変化が伴わない）ため、
      // 非表示と同時に現在位置に対して履歴登録を行うことで、
      // 疑似的に規約文書に対するブラウザバック操作とする
      if(document.querySelector('#Documents')) {
        //console.log("detected: documents");
        this.window.history.pushState("", "", null);
        setShowDoc("");
        return;
      }
      // サブメニューが表示されている場合
      // サブメニューを閉じて通常の画面遷移を行う
      else if (document.querySelector('#LoginSubMenuList')) {
        //console.log("detected: submenu");
        setLanguageMenuOpen(false);
      }
      // ブラウザ操作が行われたことを記録
      browser_ope.current = true;
      setPageStatus(window.location.pathname);
    });
  }, []);

  useEffect( () => {
    // 当該アプリはPageStatusを更新することで、疑似的に画面遷移を行っている
    // 当然そのままでは履歴には保存されないため、page_statusの更新とともに
    // historyへ記録する
    /*console.log("### ----------------------------");
    console.log("### 画面遷移検知");
    console.log("### length: " + window.history.length);
    console.log("### pathname: " + window.location.pathname);
    console.log("### page_status: " + page_status);
    console.log("### ----------------------------");*/
    // 現在の状態を記録
    history_store.current = {
      'pathname': window.location.pathname,
      'page_status': page_status
    }
    // ブラウザ操作により遷移した場合は履歴は操作しない
    if (!browser_ope.current) {
      // 遷移先に「/」が指定された場合、履歴とURLを「login」に書き換える
      if (window.location.pathname === "/") {
        //console.log("### replace: / -> /login");
        window.history.replaceState("", "", "/login");
      }
      // 現在パスが[login, register/form, password/recover]の場合のみ
      // 履歴を追加する
      else if (["/login", "/register/form", "/password/recover"].indexOf(window.location.pathname) >= 0) {
        //console.log("### push history");
        window.history.pushState("", "", page_status + window.location.search);
      }
      // 上記以外の場合履歴を上書きする
      else {
        //console.log("### replace history");
        window.history.replaceState("", "", page_status + window.location.search);
      }
    }
    else {
      //console.log("### browser operation");
      browser_ope.current = false;
    }
  }, [page_status]);

  const openDoc = async (doc) => {
    if( doc.document_id && doc.country ) {
      await loadDocument( doc.document_id, doc.country, config.agreement.register );
    }

    setShowDoc(doc);
    
  };

  // 再同意用利用規約を開く
  const openAdditionalTos = async (tos, country) => {
    if( tos.document_id && tos.country ) {
      await loadDocument( tos.document_id, tos.country, config.additional_agreement[country] );
    }

    setShowDoc(tos);

    // 再同意用利用規約表示フラグをセット
    // 表示ならばtrue、非表示ならばfalse
    if( tos ) {
      setAdditionalTosOpen(true);
    } else {
      setAdditionalTosOpen(false);
    }
  };

  const isShowDoc = () => {
    return show_doc.document_id && ! global_error;
  }

  const isMenuOpen = () => {
    return language_menu_open || (isShowDoc() && !additional_tos_open) ;
  };

  const handleMenu = () => {
    if( isShowDoc() && !additional_tos_open ) {
      setShowDoc("");
    }
    else {
      setLanguageMenuOpen(!language_menu_open);
    }
  };

  // 暗号化pUserId取得処理
  const getEncryptedPUserId = async (email) => {
    const result = await requestV2(`${AppConfig.API_URL}/api/v2/util/encrypted-p-user-id`, {
      method: "POST",
      body: JSON.stringify({
        email: email
      })
    })
      .then(res => {
        return res.json()
          .then(json => {
            if (res.status === 200) {
              return json.encryptedPUserId;
            }
            throw new ApplicationError({ code: 'system_error' }, `System error. Unexpected error occurred. (${formatError(res.status, json)})`);
          });
      })
      .catch(err => {
        if (err instanceof ApplicationError) {
          throw err;
        }
        throw new ApplicationError({ code: 'fetch_error' }, `Fetch error. (${err.message})`);
      });
    return result;
  }

  // パスワードリセット画面へ遷移する処理
  const redirectToPasswordReset = async (email) => {
    const encrypted_p_user_id = await getEncryptedPUserId(email)
    window.location.href = `${AppConfig.PW_MIG_URL}/pw_mig?lang=${lang}&client_id=${config.client_id}&return_url=${window.location.href}&uid=${encrypted_p_user_id}`;
  }

  if( global_error && ! loading ) {
    return (
    <div id="container" className="background_layer">
    <LanguageProvider value={{lang, tags, getTag, setLang, changeLanguage }}>
    <TitleProvider value={{header_title, setTitle}}>
    <div id="foreground_layer" className="invisible">
      <div id="Progress" className="invisible">
        <span className="loader"></span>
      </div>
    </div>
    <div id="contents_header"></div>
    <div id="contents_body">
      <div id="cb_foreground_layer"></div>
      <div id="body_header">
        <h1 className="header_title">
          <span className="label">{header_title}</span>
          <span className="logo" ></span>
          <div className={ isMenuOpen() ? "menu cancel" : "menu" } onClick={handleMenu}><span></span></div>
        </h1>
      </div>
      <div id="body_content">
      <div id="bc_foreground_layer" className={ language_menu_open ? "active" : undefined }>
        { language_menu_open && <Language config={config} /> }
      </div>

      <ErrorComponent title= {getTag("error.common_title")} error={getTag("error."+global_error) || global_error } />
    </div>
    <div id="contents_footer"></div>
    </div>
    </TitleProvider>
    </LanguageProvider>
    </div>
    );
  }

  if(loading) {
      return <Loading />;
  }

  return (
    <div id="container" className="background_layer">
    <LanguageProvider value={{lang, tags, getTag, setLang, changeLanguage }}>
    <TitleProvider value={{header_title, setTitle}}>
    <UtilProvider value={{ email_is_valid, phone_is_valid, request }}>
    <UserDataProvider value={{ user_data, setUserData, clearUserData }}>
    <StatusProvider value={{ page_status, setPageStatus }}>
    <div id="foreground_layer" className="invisible">
      <div id="Progress" className="invisible">
        <span className="loader"></span>
      </div>
    </div>
    <div id="contents_header"></div>
    <div id="contents_body">
      <div id="cb_foreground_layer"></div>
      <div id="body_header">
        <h1 className="header_title">
          <span className="label">{header_title}</span>
          <span className="logo" ></span>
          { config.newsEnabled && <a href={`${AppConfig.NEWS_URL}/news`}><div className={`news_icon ${(isMenuOpen()) ? "hidden" : ""}`}><span></span></div></a> }
          <div className={ isMenuOpen() ? "menu cancel" : "menu" } onClick={handleMenu}><span></span></div>
        </h1>
      </div>
      <div id="body_content">
        <div id="bc_foreground_layer" className={ language_menu_open ? "active" : undefined }>
          { language_menu_open && <Language config={config} /> }
        </div>
        { page_status.match(/^\/login/) && <Login disableAcceptBtn={disableAcceptBtn} login={login} socialLogin={socialLogin} config={config} client_conns={auth0_sns_conns} view_disabled={view_disabled} redirectToPasswordReset={redirectToPasswordReset} default_phone_region={default_phone_region} loockupPhoneNo={loockupPhoneNo}/> }
        { page_status.match(/^\/register/) && <RegistrationRouter disableAcceptBtn={disableAcceptBtn} socialLogin={socialLogin} loadRegisterDocuments={loadRegisterDocuments} config={config} registerEmail={registerEmail} registerCode={registerCode} registerPhoneCode={registerPhoneCode} registerPhone={registerPhone} registerUser={registerUser} registerPhoneUser={registerPhoneUser} show_doc={show_doc} openDoc={openDoc} openAdditionalTos={openAdditionalTos} login={login} setGlobalError={setGlobalError} client_conns={auth0_sns_conns} view_disabled={view_disabled} checkPasswordPolicy={checkPasswordPolicy} password_valid={password_valid} setPasswordValid={setPasswordValid} checkRecaptcha={checkRecaptcha} default_phone_region={default_phone_region} /> }
        { page_status.match(/^\/password/) && <PasswordResetRouter disableAcceptBtn={disableAcceptBtn} passwordResetEmail={passwordResetEmail} passwordResetPhone={passwordResetPhone} passwordResetCode={passwordResetCode} passwordResetPhoneCode={passwordResetPhoneCode} passwordReset={passwordReset} passwordPhoneReset={passwordPhoneReset} setGlobalError={setGlobalError} checkPasswordPolicy={checkPasswordPolicy} password_valid={password_valid} setPasswordValid={setPasswordValid} checkRecaptcha={checkRecaptcha} default_phone_region={default_phone_region} view_disabled={view_disabled} config={config}/> }
      </div>
    </div>
    <div id="contents_footer"></div>
    </StatusProvider>
    </UserDataProvider>
    </UtilProvider>
    </TitleProvider>
    </LanguageProvider>
    </div>
  );
}

